import { DateTile, Skeleton, formatEventWeekdayTime, readerTimezone } from '@h2hspark/ui';
import { ArrowRight, Clock, MapPin, Users } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { cardAccentFor, cityLine, eventPagePath } from '../event-discovery/constants';
import type { DiscoverEventCard } from '../event-discovery/types';

import { useOrganizerEvents } from './api';
import { PROFILE_COPY, eventDateTile, pastEventLocationLine, upcomingRowTags } from './constants';

/**
 * The two event lists on the organizer public profile (`MEM-018` AC-2, AC-3).
 *
 * ## Transcribed from `Web App.dc.html` (10 September), `showOrgProfile`, §2.8
 *
 * ```
 * upcoming h2 "Upcoming events" var(--text-2xl); p "Everything on the calendar right now."
 *          var(--text-md) --text-muted; grid repeat(3, minmax(0,1fr)); gap 24px
 * card     the event card of §3.2 — the same one Discover draws
 * past     h2 "Past events" var(--text-2xl); p; one bordered card,
 *          rows divided by --divider, padding 16px 20px
 *   tile   48px; mon on --surface-sunken; day on --surface-page-alt
 *   body   name font-brand 700 var(--text-md); sub "{time} · {city}"
 * ```
 *
 * ## The upcoming grid is `EventCard`, and that is the point
 *
 * §3.2 is one card with two variants, and the organizer's is the Discover card with a status
 * badge where the avatar stack goes. `building-from-designs.md` is explicit that a screen sharing
 * a shape with another screen uses the same component for it, so the grid renders
 * `event-discovery`'s `EventCard` rather than a second card that would drift from it — the same
 * accent bar, the same date tile, the same atmosphere pill, the same *View →*. Three columns at
 * `lg`, two at `md`, one on a phone (`MEM-056` AC-2's reference table).
 *
 * ## Four deliberate divergences, each with its reason
 *
 * **No `N going` on an upcoming card and no `{n} attended` on a past row.** Both are attendance
 * aggregates over a single room, published to anybody on the internet with no floor beneath them.
 * `MEM-018` AC-7 and `security-invariants#member-data-boundary` withhold an aggregate contributed
 * to by fewer than five members entirely rather than rounding or ranging it. The API does not
 * serve the figure at all, so this is a rule the payload carries rather than one this file
 * remembers — `EventCard` is passed no `goingCount`, and there is none to pass.
 *
 * **No status badge** (`2 seats left` / `Open`) in the card's footer. It is capacity arithmetic,
 * which `MEM-014` AC-3's note keeps off member surfaces — "exposing capacity to members would
 * invite counting seats". The footer keeps `EventCard`'s organizer row, which on this page names
 * the organizer whose profile it is; a badge and a name competing for one line is the truncation
 * defect the parity pass called the most visible in the app.
 *
 * **A past row is not a link.** The prototype's past rows carry no chevron and no handler either.
 * The event page would resolve — a past published event still has one — but a list of history is
 * read, not navigated, and giving half the rows an affordance the other half lacks is what made
 * the match cards read as broken.
 *
 * **Each list says something when it is empty.** The prototype has no empty state for either,
 * because its fixture organizer always has both. An empty region reads as a broken page, and "0
 * events" reads as a judgement on the organizer.
 */
export function OrganizerEventsPanel({ slug }: { readonly slug: string }): React.ReactElement {
  const query = useOrganizerEvents(slug);

  if (query.isPending) {
    return (
      <div className="space-y-4" aria-busy>
        <Skeleton className="h-8 w-52" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-[210px] w-full rounded-xl" />
          <Skeleton className="h-[210px] w-full rounded-xl" />
          <Skeleton className="h-[210px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (query.isError || query.data === undefined) {
    // The header above this panel resolved, so the organizer exists and only their calendar could
    // not be read. Saying so is better than an empty "Upcoming events" heading, which would tell
    // a member this organizer has nothing on — a claim nobody established.
    return (
      <p className="rounded-lg bg-muted/60 p-3.5 text-sm text-muted-foreground">
        We could not load this organizer&rsquo;s events just now. Try again in a moment.
      </p>
    );
  }

  const { upcoming, past, pastAttendedCounts, incompleteReason } = query.data;

  return (
    <div>
      <section>
        <h2 className="mb-4 text-xl md:text-2xl">{PROFILE_COPY.upcomingHeading}</h2>
        {upcoming.length === 0 ? (
          <p className="rounded-lg bg-muted/60 p-3.5 text-sm text-muted-foreground">
            {PROFILE_COPY.noUpcoming}
          </p>
        ) : (
          <ul className="space-y-4">
            {upcoming.map((card) => (
              <li key={card.eventId} className="min-w-0">
                <UpcomingRow card={card} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10">
        <h2 className="mb-4 text-xl md:text-2xl">{PROFILE_COPY.pastHeading}</h2>
        {past.length === 0 ? (
          <p className="rounded-lg bg-muted/60 p-3.5 text-sm text-muted-foreground">
            {PROFILE_COPY.noPast}
          </p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm">
            {past.map((card) => (
              <li key={card.eventId} className="border-b border-divider last:border-b-0">
                <PastRow card={card} attended={pastAttendedCounts[card.eventId] ?? null} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {incompleteReason === null ? null : (
        <p className="mt-4 text-xs text-text-subtle">{incompleteReason}</p>
      )}
    </div>
  );
}

/**
 * **One upcoming event, as one full-width row** (`MEM-060` AC-6, Brad's design of 27 October,
 * TW 44540823): the date tile, the name, the time and the place, the atmosphere, three tags and
 * "+N more", and View event at the right. It replaced a grid of Discover's cards, which put an
 * organizer's own calendar in three narrow columns.
 */
function UpcomingRow({ card }: { readonly card: DiscoverEventCard }): React.ReactElement {
  const reader = React.useMemo(() => readerTimezone(), []);
  const when = formatEventWeekdayTime({ startsAt: card.startsAt, timezone: card.timezone, reader });
  const where = cityLine(card);
  const tags = upcomingRowTags(card.interestTags);

  return (
    <div className="relative flex flex-wrap items-center gap-4 overflow-hidden rounded-xl border border-border-subtle bg-card py-4 pl-6 pr-5 shadow-sm">
      {/* The design's coloured edge: Discover's own accent for this event, so an event wears one
          colour wherever a member meets it. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: cardAccentFor(card.eventId) }}
      />
      <DateTile variant="soft" startsAt={card.startsAt} timezone={card.timezone} />
      <div className="min-w-0 flex-1 basis-64">
        <h3 className="mb-1.5 text-lg leading-[1.2]">{card.name}</h3>
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm text-muted-foreground">
          {when.length === 0 ? null : (
            <span className="inline-flex items-center gap-[5px]">
              <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {when}
            </span>
          )}
          {where === null ? null : (
            <span className="inline-flex items-center gap-[5px]">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {where}
            </span>
          )}
          {card.atmosphere === null ? null : (
            <span className="rounded-pill bg-muted px-[10px] py-[3px] font-brand text-2xs font-bold text-text-body">
              {card.atmosphere.displayName}
            </span>
          )}
        </div>
        {tags.shown.length === 0 ? null : (
          <ul className="mt-2 flex flex-wrap items-center gap-1.5">
            {tags.shown.map((tag) => (
              <li
                key={tag.termId}
                className="rounded-pill bg-muted px-[10px] py-[3px] font-brand text-2xs font-semibold text-text-body"
              >
                {tag.displayName}
              </li>
            ))}
            {tags.more === 0 ? null : (
              <li className="text-2xs text-muted-foreground">{PROFILE_COPY.moreTags(tags.more)}</li>
            )}
          </ul>
        )}
      </div>
      <Link
        to={eventPagePath(card)}
        className="inline-flex h-10 flex-none items-center gap-1.5 rounded-pill bg-primary px-5 font-brand text-sm font-bold text-primary-foreground transition-[filter] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {PROFILE_COPY.viewEvent}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </div>
  );
}

function PastRow({
  card,
  attended,
}: {
  readonly card: DiscoverEventCard;
  /** From five checked in, or `null` — the API withholds a smaller room (`MEM-060` AC-7). */
  readonly attended: number | null;
}): React.ReactElement {
  const tile = eventDateTile({ startsAt: card.startsAt, timezone: card.timezone });
  const where = pastEventLocationLine(card);

  return (
    <div className="flex items-center gap-4 px-5 py-4">
      {tile === null ? null : (
        <span
          aria-hidden
          className="flex w-12 flex-none flex-col items-center overflow-hidden rounded-md"
        >
          <span className="w-full bg-muted py-[3px] text-center font-brand text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
            {tile.month}
          </span>
          <span className="w-full bg-ivory-100 py-[2px] text-center font-brand text-lg font-black leading-[1.1] text-text-body">
            {tile.day}
          </span>
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-brand text-md font-bold text-brand">{card.name}</div>
        {where === null && attended === null ? null : (
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
            {where === null ? null : (
              <span className="inline-flex items-center gap-[5px]">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {where}
              </span>
            )}
            {attended === null ? null : (
              <span className="inline-flex items-center gap-[5px]">
                <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {PROFILE_COPY.attended(attended)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
